import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getContent } from "../../api/sanity/utils";
import { useLanguage } from "../../context";
import {
  getCookie,
  languageStorage,
  setLocalStorage,
} from "../../helpers/localStorage";
import {
  getCurrentLanguage,
  getTitle,
  queryAndSet,
  useDocumentTitle,
} from "../../helpers/utils";
import { setFooterData } from "../../redux/reducers/footer";
import { setStartTrialData } from "../../redux/reducers/startTrial";
import { useTranslation } from "..";
import { useDomainConfiguration } from "src/hooks/common/useDomainConfiguration";
import { useMyDomainConfiguration } from "src/redux/hooks/hooks";
import { Header } from "src/newComponents/layout/Header";
import { Sidebar } from "src/newComponents/layout/Sidebar";
import { SidebarV2 } from "src/newComponents/layout/SidebarV2";
import EntryScreenLoader from "src/newComponents/EntryScreenLoader";
import { useActiveFilters } from "src/context/ActiveFiltersContext";
import { useTokenValidation, TOKEN_STATE } from "src/hooks/common/useTokenValidation";
import TokenErrorPage from "./TokenErrorPage";

const TAB_STORAGE_PREFIX = "metua:connected-tabs:v1";
const HOME_TAB = { path: "/my-data/dashboard", label: "Dashboard", closable: false };

const TAB_LABEL_RULES = [
  { pattern: /^\/my-data\/dashboard$/, label: "Dashboard" },
  { pattern: /^\/my-data\/fleet-operation$/, label: "Dashboard operation" },
  { pattern: /^\/my-data\/monitoring-manager$/, label: "Diagnostic and monitoring manager" },
  { pattern: /^\/my-data\/tracking$/, label: "Suivi des vehicules" },
  { pattern: /^\/my-data\/tracking-v2$/, label: "Suivi des vehicules V2" },
  { pattern: /^\/my-data\/event-center$/, label: "Event Center" },
  { pattern: /^\/my-data\/event-center-v2$/, label: "Event Center V2" },
  { pattern: /^\/my-data\/dashcam$/, label: "Dashcam" },
  { pattern: /^\/my-data\/dashcam-v2$/, label: "Dashcam V2" },
  { pattern: /^\/my-data\/my-vehicles$/, label: "Vehicules" },
  { pattern: /^\/my-data\/ais-140$/, label: "AIS-140" },
  { pattern: /^\/my-data\/business$/, label: "Business" },
  { pattern: /^\/my-data\/business\/add-user$/, label: "Add User" },
  { pattern: /^\/my-data\/business\/add-device$/, label: "Add Device" },
  { pattern: /^\/my-data\/business\/change-imei$/, label: "Change IMEI" },
  { pattern: /^\/my-data\/reports$/, label: "Rapports & Analytique" },
  { pattern: /^\/my-data\/reports\/[^/]+$/, label: "Report Details" },
  { pattern: /^\/my-data\/new-reports$/, label: "Nouveaux rapports" },
  { pattern: /^\/my-data\/support-ticket$/, label: "Support Ticket" },
  { pattern: /^\/my-data\/support-ticket\/[^/]+$/, label: "Support Ticket" },
  { pattern: /^\/my-data\/garage$/, label: "Garage" },
  { pattern: /^\/my-data\/garage-detail\/[^/]+$/, label: "Garage Details" },
  { pattern: /^\/my-data\/traffic-dashboard$/, label: "Traffic Dashboard" },
  { pattern: /^\/my-data\/traffic-lite$/, label: "Traffic Lite" },
  { pattern: /^\/my-data\/fleetview-school-transport$/, label: "Fleetview School Transport" },
  { pattern: /^\/my-data\/vehicle-kpis(?:\/[^/]+)?$/, label: "Vehicle KPI" },
  { pattern: /^\/my-data\/places$/, label: "Places" },
  { pattern: /^\/my-data\/place\/create$/, label: "Create Place" },
  { pattern: /^\/my-data\/routes$/, label: "Routes" },
  { pattern: /^\/my-data\/route\/create$/, label: "Create Route" },
  { pattern: /^\/my-data\/voyages$/, label: "Voyages" },
  { pattern: /^\/my-data\/voyage\/create$/, label: "Create Voyage" },
  { pattern: /^\/my-data\/alerts$/, label: "Alerts" },
  { pattern: /^\/my-data\/notifications$/, label: "Notifications" },
  { pattern: /^\/my-data\/settings$/, label: "Settings" },
  { pattern: /^\/my-data\/activity-history$/, label: "Activity History" },
  { pattern: /^\/my-data\/command-results$/, label: "Command Results" },
  { pattern: /^\/my-data\/ais-navigator\/?$/, label: "AIS Navigator" },
  { pattern: /^\/my-data\/ais-navigator\/vehicles$/, label: "AIS Vehicles" },
  { pattern: /^\/my-data\/ais-navigator\/drivers$/, label: "AIS Drivers" },
  { pattern: /^\/my-data\/ais-navigator\/clients$/, label: "AIS Clients" },
  { pattern: /^\/my-data\/ais-navigator\/insurance$/, label: "AIS Insurance" },
  { pattern: /^\/my-data\/ais-navigator\/inspection-centers$/, label: "AIS Inspection Centers" },
];


const normalizeTabPath = (pathname) => {
  const s = String(pathname || "").trim();
  if (!s || s === "/my-data/") return "/my-data";
  return s.replace(/\/+$/, "");
};

const humanizeTabSegment = (segment) => {
  const acronyms = { ais: "AIS", imei: "IMEI", kpi: "KPI", kpis: "KPIs" };
  return String(segment || "Home")
    .split("-")
    .filter(Boolean)
    .map((p) => acronyms[p.toLowerCase()] || `${p.charAt(0).toUpperCase()}${p.slice(1)}`)
    .join(" ");
};

const resolveTabDefinition = (pathname) => {
  const normalizedPath = normalizeTabPath(pathname);
  const rule = TAB_LABEL_RULES.find((r) => r.pattern.test(normalizedPath));

  if (rule) {
    return {
      path: rule.canonicalPath || normalizedPath,
      label: rule.label,
      closable: rule.closable ?? normalizedPath !== HOME_TAB.path,
    };
  }

  const segments = normalizedPath.split("/").filter(Boolean);
  return {
    path: normalizedPath,
    label: humanizeTabSegment(segments[segments.length - 1] || "home"),
    closable: normalizedPath !== HOME_TAB.path,
  };
};

const ensureHomeTab = (tabs = []) => {
  const unique = [];
  const seen = new Set([HOME_TAB.path]);

  (Array.isArray(tabs) ? tabs : []).forEach((tab) => {
    const fallback = resolveTabDefinition(tab?.path);
    const path = normalizeTabPath(tab?.path || fallback.path);
    if (!path || path === HOME_TAB.path || seen.has(path)) return;
    seen.add(path);
    unique.push({ path, label: tab?.label || fallback.label, closable: tab?.closable ?? fallback.closable });
  });

  return [HOME_TAB, ...unique];
};

const readStoredTabs = (storageKey) => {
  if (typeof window === "undefined") return [HOME_TAB];
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    return raw ? ensureHomeTab(JSON.parse(raw)) : [HOME_TAB];
  } catch {
    return [HOME_TAB];
  }
};



const ConnectedPageTabs = ({ tabs, activePath, onNavigate, onClose }) => (
  <div className="bg-[#f0f0f0] px-2 py-1.5">
    <div className="no-scrollbar flex items-center gap-2 overflow-x-auto">
      {tabs.map((tab) => {
        const isActive = normalizeTabPath(activePath) === normalizeTabPath(tab.path);
        return (
          <div
            key={tab.path}
            className={`group inline-flex h-[24px] shrink-0 items-center gap-2 rounded-md border px-3 text-[12px] font-medium transition-colors ${isActive
              ? "border-[#10b981] bg-[#10b981] text-white shadow-[0_8px_20px_rgba(17,142,234,0.18)]"
              : "border-[#d5dce5] bg-white text-[#2f3a46] hover:border-[#b7c6d5] hover:bg-[#f9fbfc]"
              }`}
          >
            <button
              type="button"
              onClick={() => onNavigate(tab.path)}
              className="min-w-0"
              aria-current={isActive ? "page" : undefined}
            >
              <span className="block max-w-[180px] truncate text-left">{tab.label}</span>
            </button>
            {tab.closable && (
              <button
                type="button"
                onClick={(e) => onClose(e, tab.path)}
                className={`inline-flex h-4 w-4 items-center justify-center rounded-sm text-[11px] transition-colors ${isActive
                  ? "text-white/90 hover:bg-white/15 hover:text-white"
                  : "text-[#5f7084] hover:bg-[#eef3f8] hover:text-[#1f2937]"
                  }`}
                aria-label={`Fermer l'onglet ${tab.label}`}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// ─── TokenValidationGate ──────────────────────────────────────────────────────

/**
 * Renders children when the token is valid.
 * Renders an error page otherwise (after loading).
 */
const TokenValidationGate = ({ isV2Route, children }) => {
  const location = useLocation();
  const { tokenState, retry } = useTokenValidation({
    pathname: location.pathname,
    search: location.search,
    isV2Route,
  });

  if (tokenState === TOKEN_STATE.IDLE || tokenState === TOKEN_STATE.LOADING) {
    return <EntryScreenLoader />;
  }

  if (tokenState !== TOKEN_STATE.VALID) {
    return <TokenErrorPage tokenState={tokenState} onRetry={retry} />;
  }

  return children;
};

// ─── AppData ──────────────────────────────────────────────────────────────────

const AppData = () => {
  const { language, updateLanguage } = useLanguage();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const location = useLocation();
  const { t } = useTranslation();
  const intl = useIntl();
  const navigate = useNavigate();

  const isEmbeddedControlRoom = useMemo(
    () => new URLSearchParams(location.search).get("embed") === "control-room",
    [location.search]
  );

  const isAisNavigatorRoute = pathname.startsWith("/my-data/ais-navigator");
  const isV2Route = pathname.toLowerCase().includes("-v2");

  // ── Sidebar state ────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // ── Date filter state ────────────────────────────────────────────────────
  const [selectedFilter, setSelectedFilter] = useState("lastDay");


  // ── AOI state ────────────────────────────────────────────────────────────
  const [allAreas, setAllAreas] = useState([]);

  // ── Filter state ─────────────────────────────────────────────────────────
  const [filters, setFilters] = useState([]);
  const [site, setSite] = useState([]);
  const [zone, setZone] = useState([]);

  const { activeFilters, currentSelection } = useActiveFilters();

  // ── Tabs ─────────────────────────────────────────────────────────────────
  const currentUserId = getCookie("userId") || "anonymous";
  const tabStorageKey = `${TAB_STORAGE_PREFIX}:${currentUserId}`;
  const [openTabs, setOpenTabs] = useState(() => readStoredTabs(tabStorageKey));

  useDomainConfiguration();
  const { config } = useMyDomainConfiguration();

  // // ── Redirect to login if no token at all ─────────────────────────────────
  // useEffect(() => {
  //   if (!getCookie("token")) {
  //     navigate("/", { replace: true });
  //   }
  // }, [location.pathname, navigate]);

  // ── Responsive sidebar ───────────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setIsExpanded(!mobile);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);


  // ── Date filter sync with active filters ─────────────────────────────────
  const DateFilter = {
    toDay: { label: "Today" },
    lastDay: { label: "Last day" },
    lastWeek: { label: "Last Week" },
    lastMonth: { label: "Last Month" },
    allTime: { label: "All Time" },
    custom: { label: "Custom" },
  };

  useEffect(() => {
    const periodLabel = String(currentSelection?.periode || "").toLowerCase();
    const matched = Object.entries(DateFilter).find(
      ([, f]) => String(f.label).toLowerCase() === periodLabel
    );
    if (matched?.[0] && matched[0] !== selectedFilter) {
      setSelectedFilter(matched[0]);
    }
  }, [currentSelection?.periode]);

  // ── Tab management ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!pathname.startsWith("/my-data")) return;
    const currentTab = resolveTabDefinition(pathname);
    setOpenTabs((prev) => {
      const prepared = ensureHomeTab(prev);
      const idx = prepared.findIndex((t) => normalizeTabPath(t.path) === currentTab.path);
      if (idx >= 0) {
        return prepared.map((t, i) =>
          i === idx ? { ...t, label: currentTab.label, closable: currentTab.closable } : t
        );
      }
      return [...prepared, currentTab];
    });
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(tabStorageKey, JSON.stringify(ensureHomeTab(openTabs)));
  }, [openTabs, tabStorageKey]);

  const handleNavigateToTab = useCallback((path) => {
    if (!path || normalizeTabPath(path) === normalizeTabPath(pathname)) return;
    navigate(path);
  }, [navigate, pathname]);

  const handleCloseTab = useCallback((event, pathToClose) => {
    event.preventDefault();
    event.stopPropagation();
    const prepared = ensureHomeTab(openTabs);
    const closingPath = normalizeTabPath(pathToClose);
    const closingTab = prepared.find((t) => normalizeTabPath(t.path) === closingPath);
    if (!closingTab?.closable) return;
    const closingIndex = prepared.findIndex((t) => normalizeTabPath(t.path) === closingPath);
    const nextTabs = prepared.filter((t) => normalizeTabPath(t.path) !== closingPath);
    setOpenTabs(nextTabs);
    if (normalizeTabPath(pathname) === closingPath) {
      const fallback = nextTabs[closingIndex - 1] || nextTabs[closingIndex] || HOME_TAB;
      navigate(fallback.path);
    }
  }, [navigate, openTabs, pathname]);


  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleAddFilter = useCallback((filter) => {
    setFilters((prev) => [...prev, filter]);
    setSite(filter);
    setZone(null);
  }, []);

  const handleAddFilterZone = useCallback((filter) => {
    setZone(filter);
  }, []);

  // ── CMS data + language ───────────────────────────────────────────────────
  const getData = useCallback(async (lang, path) => {
    const qs = await queryAndSet(path);
    if (!qs) return;
    const data = await getContent(lang, qs.query);
    if (!data) return;
    if (qs?.setData) dispatch(qs.setData(data));
    dispatch(setFooterData(data?.footer));
    dispatch(setStartTrialData(data?.startTrial));
  }, [dispatch]);

  useEffect(() => {
    const currentLanguage = getCurrentLanguage(pathname, language);
    if (currentLanguage !== language) {
      setLocalStorage(languageStorage, currentLanguage);
      updateLanguage();
    }
    getData(currentLanguage, pathname);
  }, [pathname]);

  useEffect(() => {
    if (pathname === "/") getData(language, pathname);
  }, [language, pathname]);

  useDocumentTitle(
    `${config?.header?.title ?? "Lewoo Lite"} | ${getTitle(intl, pathname)}`,
    config?.header?.shortcutIcon
  );

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!config) return <EntryScreenLoader />;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <TokenValidationGate isV2Route={isV2Route}>
      <div className="flex h-screen w-full overflow-hidden bg-background">
        {!isAisNavigatorRoute && !isEmbeddedControlRoom && (
          isV2Route ? <SidebarV2 /> : <Sidebar />
        )}

        <div className="flex flex-1 flex-col overflow-hidden">
          {!isAisNavigatorRoute && !isEmbeddedControlRoom && (
            <Header
              allAreas={allAreas}
              onAddFilter={handleAddFilter}
              onAddFilterzone={handleAddFilterZone}
            />
          )}

          {!isEmbeddedControlRoom && (
            <ConnectedPageTabs
              tabs={openTabs}
              activePath={pathname}
              onNavigate={handleNavigateToTab}
              onClose={handleCloseTab}
            />
          )}

          <main
            className={`relative flex-1 overflow-auto bg-[#f0f0f0] ${isAisNavigatorRoute || isEmbeddedControlRoom ? "" : "px-2 pb-6"
              }`}
          >
            <Outlet context={{ allAreas, filters, site, zone }} />
          </main>
        </div>
      </div>
    </TokenValidationGate>
  );
};

export default AppData;